import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { api } from '../lib/api';
import { docStatusTone } from '../lib/docs';
import { amount } from '../lib/format';
import { useSession } from '../lib/session';
import '../styles/documents.css';

interface DocRow {
  id: string;
  type: string;
  typeLabel: string;
  number: string;
  status: string;
  statusLabel: string;
  title: string | null;
  counterpartyName: string | null;
  currency: string;
  totalMinor: number;
  issueDate: string | null;
}

interface ListConfig {
  eyebrow: string;
  title: string;
  sub: string;
  types?: string;
  action?: { label: string; to: string };
}

// Configuración de la vista según la ruta. Una sola pantalla sirve a todas.
const CONFIG: Record<string, ListConfig> = {
  '/documentos': {
    eyebrow: 'Documentos',
    title: 'Expedientes',
    sub: 'Todos los documentos de la empresa, con su cadena de trazabilidad completa.',
  },
  '/ventas/cotizaciones': {
    eyebrow: 'Ventas',
    title: 'Cotizaciones',
    sub: 'Ofertas emitidas a clientes. Cada una puede convertirse en contrato o factura sin recapturar datos.',
    types: 'quote',
    action: { label: 'New quotation', to: '/ventas/cotizaciones/nueva' },
  },
  '/ventas/contratos': {
    eyebrow: 'Ventas',
    title: 'Contratos',
    sub: 'Acuerdos en firme. Del borrador a la firma, cada estado queda sellado y trazado.',
    types: 'contract',
    action: { label: 'Nuevo contrato', to: '/ventas/contratos/nuevo' },
  },
  '/ventas/facturas': {
    eyebrow: 'Ventas',
    title: 'Facturas',
    sub: 'Tax documents. Once issued they are not edited: they are corrected with a credit or debit note.',
    types: 'invoice,credit_note,debit_note',
    action: { label: 'Nueva factura', to: '/ventas/facturas/nueva' },
  },
  '/finanzas/pagos': {
    eyebrow: 'Finanzas',
    title: 'Cobros y pagos',
    sub: 'Treasury movements. Reconcile each payment with the invoices it settles.',
    types: 'payment',
    action: { label: 'Registrar cobro', to: '/finanzas/pagos/nuevo' },
  },
  '/compras/gastos': {
    eyebrow: 'Compras',
    title: 'Gastos y tickets',
    sub: 'Captured expenses. Photograph the receipt and the AI proposes the breakdown.',
    types: 'expense',
    action: { label: 'Nuevo gasto', to: '/compras/gastos/nuevo' },
  },
  '/operaciones/loi': {
    eyebrow: 'Operaciones',
    title: 'LOI e ICPO',
    sub: 'Letters of intent and irrevocable purchase orders in international trade.',
    types: 'loi,icpo,fco',
    action: { label: 'Nueva LOI', to: '/operaciones/loi/nueva' },
  },
  '/compras': {
    eyebrow: 'Compras',
    title: 'Órdenes de compra',
    sub: 'Purchase requests and orders, vendor invoices and goods receipts.',
    types: 'purchase_order,purchase_request,vendor_invoice,goods_receipt',
  },
};

function StatusPill({ status, label }: { status: string; label: string }) {
  return (
    <span className={`pill pill--${docStatusTone(status)}`}>
      <span className="pill__dot" />
      {label}
    </span>
  );
}

export function Documents() {
  const loc = useLocation();
  const nav = useNavigate();
  const { company, caps } = useSession();
  const [rows, setRows] = useState<DocRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const cfg = CONFIG[loc.pathname] ?? CONFIG['/documentos']!;

  useEffect(() => {
    if (!company) return;
    setRows(null);
    setErr(null);
    const qs = cfg.types ? `?types=${encodeURIComponent(cfg.types)}` : '';
    api
      .get<DocRow[]>(`/documents${qs}`)
      .then(setRows)
      .catch((e) => setErr(e instanceof Error ? e.message : 'Error'));
  }, [company, cfg.types, loc.pathname]);

  const statuses = useMemo(() => {
    if (!rows) return [];
    return Array.from(new Set(rows.map((r) => r.status)));
  }, [rows]);

  const filtered = useMemo(
    () => (rows ?? []).filter((r) => statusFilter === 'all' || r.status === statusFilter),
    [rows, statusFilter],
  );

  // ¿Puede crear? Se comprueba con la acción rápida equivalente del backend.
  const canCreate = cfg.action
    ? caps?.quickActions.some((a) => a.route === cfg.action!.to)
    : false;

  return (
    <main className="main">
      <div className="page-head">
        <div className="page-head__row">
          <div className="page-head__title">
            <span className="eyebrow">{cfg.eyebrow}</span>
            <h1 className="h1">{cfg.title}</h1>
            <p className="page-head__sub">{cfg.sub}</p>
          </div>
          {cfg.action && canCreate && (
            <button className="btn btn--primary" onClick={() => nav(cfg.action!.to)}>
              <Icon name="plus" size={16} />
              {cfg.action.label}
            </button>
          )}
        </div>
      </div>

      {statuses.length > 1 && (
        <div className="filterbar" style={{ marginBottom: 16 }}>
          <button
            className={`chip-toggle${statusFilter === 'all' ? ' chip-toggle--on' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            Todos <span className="chip-toggle__n">{rows?.length ?? 0}</span>
          </button>
          {statuses.map((s) => {
            const label = rows?.find((r) => r.status === s)?.statusLabel ?? s;
            const n = rows?.filter((r) => r.status === s).length ?? 0;
            return (
              <button
                key={s}
                className={`chip-toggle${statusFilter === s ? ' chip-toggle--on' : ''}`}
                onClick={() => setStatusFilter(s)}
              >
                {label} <span className="chip-toggle__n">{n}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="card">
        {err && <div className="empty">{err}</div>}
        {!err && !rows && <div className="empty">Cargando documentos…</div>}
        {!err && rows && filtered.length === 0 && (
          <div className="empty">No documents to show yet.</div>
        )}

        {!err && filtered.length > 0 && (
          <div className="dtable">
            <div className="dtable__head">
              <span>Number</span>
              <span>Contraparte</span>
              <span>Estado</span>
              <span>Fecha</span>
              <span style={{ textAlign: 'right' }}>Importe</span>
            </div>
            {filtered.map((d) => (
              <button key={d.id} className="dtable__row" onClick={() => nav(`/documentos/${d.id}`)}>
                <span className="dnum">
                  <span className="dnum__id">{d.number}</span>
                </span>
                <span className="dcp">
                  <span className="dcp__name">{d.counterpartyName ?? '—'}</span>
                  <span className="dcp__title">
                    <span className="type-tag" style={{ marginRight: 6 }}>{d.typeLabel}</span>
                    {d.title ?? ''}
                  </span>
                </span>
                <span><StatusPill status={d.status} label={d.statusLabel} /></span>
                <span className="ddate">{d.issueDate ? new Date(d.issueDate).toLocaleDateString('es-ES') : '—'}</span>
                <span className="dtotal">{amount(d.totalMinor, d.currency)} {d.currency}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
